import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { ExternalLink, BookPlus, X } from 'lucide-react';
import { useCISearch } from '../../contexts/CISearchContext';
import { ciService } from '../../services/api';
// CI reuses the MKE container layout (results table, pagination, icon
// buttons). Import its stylesheet directly so the CI page works even if
// MKE is unmounted or removed.
import '../MyKnowledgeEntries/MyKnowledgeEntries.css';
import './CollectiveIntelligence.css';

// Empty-state copy decided in the PoC spec. Threshold message takes
// precedence over user-excluded when both are non-zero — per spec.
const EMPTY_NO_MATCHES = 'No matches in the collective for that query.';
const EMPTY_BELOW_THRESHOLD =
  'The Collective Intelligence is percolating. Check back in a week!';
const EMPTY_ALL_CLAIMED =
  "You've already added every match for that query to your knowledge base.";

const CIRow = ({ entry, isSelected, isClaimed, onToggle }) => {
  // Universal scope returns lastCapturedAt as a plain YYYY-MM-DD string.
  // Parse as local time — new Date('2026-08-06') would be treated as UTC and
  // can render as the previous day west of Greenwich.
  const dateLabel = entry.lastCapturedAt
    ? format(
        new Date(`${entry.lastCapturedAt}T00:00:00`),
        'MM/dd/yyyy'
      )
    : '';

  return (
    <tbody
      className={`entry-group${isClaimed ? ' ci-row-claimed' : ''}`}
    >
      <tr className="entry-text-row">
        <td className="col-added">{dateLabel}</td>
        <td className="col-title">
          <div className="cell-content">
            {entry.document?.docTitle || 'Untitled'}
          </div>
          {entry.distinctUserCount != null && (
            <div className="ci-contributor-badge">
              {entry.distinctUserCount} contributor
              {entry.distinctUserCount === 1 ? '' : 's'}
            </div>
          )}
        </td>
        <td className="col-focus">
          <div className="cell-content">
            {entry.focus}
          </div>
        </td>
        <td className="col-add" rowSpan={2}>
          <input
            type="checkbox"
            className="ci-select-checkbox"
            checked={isClaimed ? false : isSelected}
            disabled={isClaimed}
            onChange={() =>
              onToggle(entry.focusEntryId)
            }
            aria-label={
              isClaimed
                ? 'Already in your knowledge entries'
                : 'Select entry'
            }
          />
        </td>
      </tr>
      <tr className="entry-icon-row">
        <td className="col-added" />
        <td className="col-title">
          <div className="icon-container">
            {entry.document?.docUrl && (
              <a
                href={entry.document.docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="icon-button url-button"
                title="Open document"
              >
                <ExternalLink size={20} />
              </a>
            )}
          </div>
        </td>
        <td className="col-focus" />
      </tr>
    </tbody>
  );
};

const CollectiveIntelligence = () => {
  const {
    query,
    results,
    isSearching,
    error,
    currentPage,
    setPage,
    // Selection now lives in CISearchContext so that every path which replaces
    // the result set clears it (new search, page change, source-filter toggle).
    selectedEntries,
    toggleSelection,
    clearSelection
  } = useCISearch();

  // Claimed-this-session ids, for greying rows in place. Stays LOCAL rather
  // than in the context: it is scoped to the rows currently on screen, and the
  // context's fetch-triggered clear would be wrong for it — a claimed row that
  // survives to the next fetch is gone from the server's results anyway (the
  // backend re-excludes the user's captures on every request), so this Set
  // simply becomes irrelevant rather than stale.
  const [claimedEntries, setClaimedEntries] = useState(new Set());
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // One-shot pulse on the header button, fired when selection goes from empty
  // to non-empty. The colour change alone was too quiet a signal that the
  // action had become available.
  const [pulse, setPulse] = useState(false);
  const prevSelectedSizeRef = useRef(selectedEntries.size);
  const pulseTimerRef = useRef(null);

  useEffect(() => {
    const prevSize = prevSelectedSizeRef.current;
    prevSelectedSizeRef.current = selectedEntries.size;
    // The 0 → 1+ guard is what makes this one-shot. selectedEntries is
    // copy-on-write in CISearchContext, so it gets a NEW reference on every
    // toggle and this effect runs on every checkbox click; without the guard
    // the pulse would re-fire on each one.
    if (prevSize === 0 && selectedEntries.size > 0) {
      setPulse(true);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      // setTimeout rather than onAnimationEnd deliberately: under the
      // prefers-reduced-motion rule the animation is `none`, so animationend
      // would never fire and the class would stick permanently. A timer has no
      // such dependency, and needs no 0.01ms-duration workaround.
      pulseTimerRef.current = setTimeout(() => setPulse(false), 400);
    }
  }, [selectedEntries]);

  // Both timers are fire-and-forget during a session; clear them on unmount so
  // neither calls setState on a torn-down component.
  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (value) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast(value);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, 5000);
  };

  const dismissToast = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(null);
  };

  const handleBulkClaim = async () => {
    if (selectedEntries.size === 0) return;
    const ids = Array.from(selectedEntries);
    try {
      const result = await ciService.claimBulk(ids);

      // Key on the ids the SERVER reports back, never on position against
      // `ids`: the endpoint deduplicates before processing, so `outcomes` can
      // be shorter than what was submitted.
      const outcomes = Array.isArray(result.outcomes) ? result.outcomes : [];

      if (outcomes.length > 0) {
        // Normal path: per-id detail is available, so grey exactly what settled.
        //
        // 'claimed' and 'alreadyClaimed' both mean "this is in your KB now", so
        // both grey the row. 'failed' is left untouched — still selectable, so
        // the user can retry without re-finding it.
        const settledIds = outcomes
          .filter(o => o.status === 'claimed' || o.status === 'alreadyClaimed')
          .map(o => o.focusEntryId);
        const failedCount = outcomes.filter(o => o.status === 'failed').length;

        if (settledIds.length > 0) {
          setClaimedEntries(prev => {
            const next = new Set(prev);
            for (const id of settledIds) next.add(id);
            return next;
          });
          // Drop only the settled ids, immediately — failures stay selected, and
          // we do not wait for a fetch-triggered clear that may never come.
          clearSelection(settledIds);
        }

        if (settledIds.length === 0 && failedCount > 0) {
          showToast({ added: 0, failed: failedCount });
        } else if (settledIds.length > 0) {
          showToast({ added: settledIds.length, failed: failedCount });
        }
        return;
      }

      // ---------------------------------------------------------------
      // FALLBACK: no per-id detail on the response.
      // ---------------------------------------------------------------
      // Not reachable today — backend and frontend ship from one repo and
      // there is exactly one consumer of this endpoint — but the alternative
      // to guarding is a response shape change silently producing NO greying,
      // NO toast, and NO error. The bar here is only that *something visible*
      // happens; per-id accuracy is what `outcomes` exists to provide, and it
      // is not recoverable from counters alone.
      //
      // Number()||0 rather than ?? 0: a malformed response may carry these as
      // strings, null, or nothing at all, and all of those should read as 0.
      const claimedCount = Number(result.claimed) || 0;
      const alreadyCount = Number(result.alreadyClaimed) || 0;
      const failedCount = Number(result.failed) || 0;

      // `ids` comes from a Set, so it holds no duplicates — which means the
      // server's dedupe is a no-op for it and the counters are directly
      // comparable to ids.length. That equivalence is what makes the
      // unambiguous branch below safe; it would not hold for a caller that
      // could submit duplicates.
      const allSettled =
        failedCount === 0 &&
        claimedCount + alreadyCount === ids.length;

      if (allSettled) {
        // Every submitted id settled, so "which ones" is not in question —
        // grey all of them, exactly as the per-id path would have.
        setClaimedEntries(prev => {
          const next = new Set(prev);
          for (const id of ids) next.add(id);
          return next;
        });
        clearSelection(ids);
        showToast({ added: ids.length, failed: 0 });
        return;
      }

      // Ambiguous: some ids settled and some did not, and we cannot tell
      // which. Greying the wrong rows would be a worse lie than greying none,
      // so this degrades to the pre-outcomes behaviour — rows stay ungreyed
      // and selected — and reports the counts so the result is still visible.
      //
      // `unsettled` prefers the server's own failed count; when that is 0 or
      // absent but the numbers still do not add up (including a wholly
      // malformed response, where everything is 0), it falls back to whatever
      // the counters left unaccounted for, so the toast never claims a
      // nonsensical "0 failed" or renders "Couldn't add 0 entries".
      const unsettled = failedCount > 0
        ? failedCount
        : Math.max(0, ids.length - claimedCount - alreadyCount);

      console.warn(
        'Bulk claim: response carried no per-id outcomes; ' +
        'degraded to counter-based reporting.',
        { submitted: ids.length, claimedCount, alreadyCount, failedCount }
      );

      showToast({ added: claimedCount + alreadyCount, failed: unsettled });
    } catch (err) {
      console.error('Bulk claim failed:', err);
      showToast({ added: 0, failed: ids.length });
    }
  };

  const entries = results?.entries || [];
  const totalPages = results?.totalPages || 1;
  const displayedCount = entries.length;

  const renderEmptyState = () => {
    if (!results) {
      return query
        ? 'Loading...'
        : 'Search the collective to surface focuses captured by multiple contributors.';
    }
    const raw = results.rawMatchCount || 0;
    const thresholdFiltered = results.thresholdFilteredCount || 0;
    const userExcluded = results.userExcludedCount || 0;

    if (raw === 0) {
      return EMPTY_NO_MATCHES;
    }
    if (thresholdFiltered > 0 && displayedCount === 0) {
      // Spec: prefer the threshold message when both threshold-filtered
      // and user-excluded matches exist.
      return EMPTY_BELOW_THRESHOLD;
    }
    if (userExcluded === raw && displayedCount === 0) {
      return EMPTY_ALL_CLAIMED;
    }
    if (displayedCount === 0) {
      return EMPTY_NO_MATCHES;
    }
    return null;
  };

  const emptyMessage = renderEmptyState();

  return (
    <div className="knowledge-entries ci-results">
      <div className="results-container">
        {error && <div className="error-message">{error}</div>}
        <div className="table-wrapper">
          <table className="results-list ci-results-table">
            <thead>
              <tr>
                <th className="col-added">Added</th>
                <th className="col-title">Title</th>
                <th className="col-focus">Focus</th>
                <th className="col-add">
                  <span
                    className="ci-claim-tooltip-wrap"
                    data-tooltip="Save to your knowledge entries"
                  >
                    <button
                      className={`ci-bulk-claim-button${pulse ? ' ci-pulse' : ''}`}
                      onClick={handleBulkClaim}
                      disabled={selectedEntries.size === 0}
                      aria-label="Save to your knowledge entries"
                    >
                      <BookPlus size={26} strokeWidth={2} />
                    </button>
                  </span>
                </th>
              </tr>
            </thead>
            {isSearching ? (
              <tbody>
                <tr>
                  <td colSpan="4" className="loading-message">Searching...</td>
                </tr>
              </tbody>
            ) : emptyMessage ? (
              <tbody>
                <tr>
                  <td colSpan="4" className="no-entries">{emptyMessage}</td>
                </tr>
              </tbody>
            ) : (
              entries.map(entry => (
                <CIRow
                  key={entry.focusEntryId}
                  entry={entry}
                  isSelected={selectedEntries.has(
                    entry.focusEntryId
                  )}
                  isClaimed={claimedEntries.has(
                    entry.focusEntryId
                  )}
                  onToggle={toggleSelection}
                />
              ))
            )}
          </table>
        </div>

        {toast !== null && (
          <div
            className={
              toast.added === 0
                ? 'ci-toast ci-toast-error'
                : 'ci-toast'
            }
            role="status"
          >
            <span className="ci-toast-text">
              {toast.added === 0
                ? `Couldn't add ${toast.failed} ${toast.failed === 1 ? 'entry' : 'entries'} — try again`
                : toast.failed > 0
                  ? `Added ${toast.added} to your knowledge entries; ${toast.failed} failed — try again`
                  : `Added ${toast.added} to your knowledge entries`}
            </span>
            <button
              className="ci-toast-dismiss"
              onClick={dismissToast}
              aria-label="Dismiss notification"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1 || isSearching}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || isSearching}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectiveIntelligence;
